import React, { useEffect, useRef, useState } from 'react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

function useInView(threshold = 0.15): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

const Aboutus = () => {
    const { isDark } = useTheme();
    const [headerRef, headerInView] = useInView(0.1);
    const [propRef, propInView] = useInView(0.1);
    const [cardsRef, cardsInView] = useInView(0.1);
    const [valuesRef, valuesInView] = useInView(0.1);

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .about-page { font-family: 'DM Sans', sans-serif; overflow-x: clip; transition: background-color 0.3s ease; }

        .fade-up {
          opacity: 0; transform: translateY(36px);
          transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1);
        }
        .fade-up.in { opacity: 1; transform: translateY(0); }
        .fade-up.d1 { transition-delay: 0.1s; }
        .fade-up.d2 { transition-delay: 0.2s; }
        .fade-up.d3 { transition-delay: 0.3s; }

        .prop-card {
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }

        .blob-anim {
          border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
          animation: morph 8s ease-in-out infinite alternate;
        }
        @keyframes morph {
          0% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
          100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
        }

        @media (max-width: 900px) {
          .prop-layout { flex-direction: column !important; }
          .mv-grid { grid-template-columns: 1fr !important; }
          .values-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

            <div className="about-page" style={{ background: isDark ? '#0b0614' : '#fff' }}>

                {/* ═══ HERO / HEADER ═══ */}
                <section style={{
                    background: isDark
                        ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #faf6fb 55%, #f6ecf5 100%)',
                    padding: 'clamp(90px, 12vw, 130px) 32px clamp(50px, 7vw, 70px)',
                    position: 'relative', overflow: 'hidden',
                }}>
                    <div className="blob-anim" style={{
                        position: 'absolute', top: '-80px', right: '-80px',
                        width: '420px', height: '420px',
                        background: isDark
                            ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))'
                            : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))',
                        pointerEvents: 'none',
                    }} />
                    {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                    <HexagonCornerDecoration
                        variant="horizontal-degrade"
                        direction="left-to-right"
                        opacity={isDark ? 0.70 : 0.50}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 0,
                        }}
                    />

                    <div ref={headerRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                        <h1 className={`hero-title-about fade-up d1 ${headerInView ? 'in' : ''}`} style={{
                            fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                            fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.08', letterSpacing: '-0.025em',
                            marginBottom: '20px',
                        }}>
                            Sobre <span style={{ background: 'linear-gradient(135deg, #61257b 0%, #c1348b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Nosotros</span>
                        </h1>
                        <p className={`fade-up d2 ${headerInView ? 'in' : ''}`} style={{
                            fontSize: '1.05rem', color: isDark ? '#cbd5e1' : '#64748b',
                            maxWidth: '480px', margin: '0 auto',
                            lineHeight: '1.7', fontWeight: '400',
                        }}>
                            Comprometidos con la excelencia, la innovación y el crecimiento colectivo en Bolivia.
                        </p>
                        <div className={`fade-up d3 ${headerInView ? 'in' : ''}`} style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                            <BrandStripes variant="scroll-prompt" size="md" promptText="Conoce nuestra historia" />
                        </div>
                    </div>
                </section>

                {/* ═══ NUESTRO PROPÓSITO ═══ */}
                <section style={{ background: isDark ? '#0b0614' : '#fff', padding: '80px 32px' }}>
                    <div ref={propRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto' }}>

                        <div className={`fade-up ${propInView ? 'in' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <span style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.secondary }}>
                                Nuestro Propósito
                            </span>
                            <div style={{ height: '2px', width: '48px', borderRadius: '2px', background: colors.secondary }} />
                        </div>

                        <div className="prop-layout" style={{ display: 'flex', gap: '56px', alignItems: 'center' }}>
                            <div className={`fade-up d1 ${propInView ? 'in' : ''}`} style={{ flex: '1', minWidth: '280px' }}>
                                <h2 style={{
                                    fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: '800',
                                    color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.15', letterSpacing: '-0.02em',
                                    marginBottom: '24px',
                                }}>
                                    Creamos sinergias para{' '}
                                    <span style={{ color: colors.secondary }}>impulsar el crecimiento</span>
                                </h2>
                                <p style={{ fontSize: '1rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85' }}>
                                    Crear sinergias entre emprendedores, startups, pequeñas, medianas y grandes empresas, articulando un ecosistema donde cada actor colabora y aporta al crecimiento colectivo, impulsando la innovación y el desarrollo de nuevas ideas con impacto.
                                </p>
                            </div>

                            <div className={`fade-up d2 ${propInView ? 'in' : ''}`} style={{ flex: '1', minWidth: '280px', maxWidth: '480px' }}>
                                <BorderGlow
                                    borderRadius={28}
                                    backgroundColor={isDark ? '#150d24' : '#ffffff'}
                                    style={{ width: '100%' }}
                                >
                                    <div style={{
                                        background: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.primary})`,
                                        borderRadius: '26px', padding: '40px 36px',
                                        position: 'relative', overflow: 'hidden',
                                    }}>
                                        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
                                        <div style={{ position: 'absolute', bottom: '-50px', left: '-30px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
                                        <div style={{
                                            width: '52px', height: '52px', borderRadius: '16px',
                                            background: 'rgba(255,255,255,0.18)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '1.7rem', marginBottom: '20px',
                                        }}>🎯</div>
                                        <div style={{ fontSize: '1.35rem', fontWeight: '800', color: '#fff', lineHeight: '1.3', marginBottom: '14px' }}>
                                            Ecosistema de colaboración real
                                        </div>
                                        <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.85)', lineHeight: '1.75' }}>
                                            Articulamos actores del ecosistema emprendedor para que cada uno colabore, aporte y crezca colectivamente.
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '24px' }}>
                                            {['Innovación', 'Impacto', 'Colaboración'].map(tag => (
                                                <span key={tag} style={{
                                                    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
                                                    borderRadius: '100px', padding: '4px 14px',
                                                    fontSize: '0.78rem', fontWeight: '600', color: '#fff',
                                                }}>{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                </BorderGlow>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═══ MISIÓN Y VISIÓN ═══ */}
                <section style={{ background: isDark ? '#0e071a' : '#f8fafc', padding: '80px 32px', position: 'relative', overflow: 'hidden' }}>
                    <div ref={cardsRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div className={`fade-up ${cardsInView ? 'in' : ''}`} style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
                                <div style={{ height: '2px', width: '40px', background: colors.secondary, borderRadius: '2px' }} />
                                <span style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.secondary }}>
                                    Pilares Estratégicos
                                </span>
                                <div style={{ height: '2px', width: '40px', background: colors.secondary, borderRadius: '2px' }} />
                            </div>
                            <h2 style={{ fontSize: 'clamp(1.7rem, 2.8vw, 2.3rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                                Misión <span style={{ color: '#94a3b8', fontWeight: '400' }}>&</span> Visión
                            </h2>
                        </div>

                        <div className="mv-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                            {/* Misión con BorderGlow */}
                            <div className={`fade-up d1 ${cardsInView ? 'in' : ''}`}>
                                <BorderGlow
                                    borderRadius={24}
                                    backgroundColor={isDark ? '#150d24' : '#ffffff'}
                                    style={{ width: '100%', height: '100%' }}
                                >
                                    <div style={{
                                        padding: '36px 32px', position: 'relative', overflow: 'hidden', height: '100%',
                                        display: 'flex', flexDirection: 'column',
                                    }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg, ${colors.secondary}, ${colors.secondaryLight})`, borderRadius: '24px 24px 0 0' }} />
                                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: `linear-gradient(135deg, ${colors.secondary}20, ${colors.secondaryLight}20)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '20px' }}>🚀</div>
                                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '8px', letterSpacing: '-0.01em' }}>Misión</h3>
                                        <div style={{ width: '36px', height: '3px', background: colors.secondary, borderRadius: '2px', marginBottom: '20px' }} />
                                        <p style={{ fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85', marginBottom: '24px', flex: 1 }}>
                                            Impulsar el crecimiento de emprendedores bolivianos ofreciendo espacios colaborativos, incubación de negocios y gestión de proyectos innovadores, promoviendo su éxito y transformación digital.
                                        </p>
                                        <div style={{ borderLeft: `3px solid ${colors.secondary}`, paddingLeft: '14px', fontSize: '0.85rem', fontWeight: '600', color: colors.secondary, lineHeight: '1.6' }}>
                                            Innovación práctica · Valor tangible · Excelencia continua
                                        </div>
                                    </div>
                                </BorderGlow>
                            </div>

                            {/* Visión con BorderGlow */}
                            <div className={`fade-up d2 ${cardsInView ? 'in' : ''}`}>
                                <BorderGlow
                                    borderRadius={24}
                                    backgroundColor={isDark ? '#150d24' : '#ffffff'}
                                    style={{ width: '100%', height: '100%' }}
                                >
                                    <div style={{
                                        padding: '36px 32px', position: 'relative', overflow: 'hidden', height: '100%',
                                        display: 'flex', flexDirection: 'column',
                                    }}>
                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg, ${colors.accentDark}, ${colors.accent})`, borderRadius: '24px 24px 0 0' }} />
                                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: `linear-gradient(135deg, ${colors.accentDark}20, ${colors.accent}30)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '20px' }}>🌟</div>
                                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '8px', letterSpacing: '-0.01em' }}>Visión</h3>
                                        <div style={{ width: '36px', height: '3px', background: colors.accentDark, borderRadius: '2px', marginBottom: '20px' }} />
                                        <p style={{ fontSize: '0.97rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85', marginBottom: '24px', flex: 1 }}>
                                            Ser el ecosistema líder en Bolivia para emprendedores, startups y profesionales, integrando coworking, incubación de negocios y gestión de proyectos innovadores, con un enfoque en colaboración, impacto y transformación digital sostenible.
                                        </p>
                                        <div style={{ borderLeft: `3px solid ${colors.accentDark}`, paddingLeft: '14px', fontSize: '0.85rem', fontWeight: '600', color: colors.accentDark, lineHeight: '1.6' }}>
                                            Liderazgo global · Impacto social · Tecnología ética
                                        </div>
                                    </div>
                                </BorderGlow>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ═══ VALORES ═══ */}
                <section style={{ background: isDark ? '#0b0614' : '#fff', padding: '80px 32px 100px' }}>
                    <div ref={valuesRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto' }}>

                        <div className={`fade-up ${valuesInView ? 'in' : ''}`} style={{ marginBottom: '48px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.primary }}>
                                    Lo que nos define
                                </span>
                                <div style={{ height: '2px', width: '48px', borderRadius: '2px', background: colors.primary }} />
                            </div>
                            <h2 style={{ fontSize: 'clamp(1.7rem, 2.8vw, 2.3rem)', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                                Nuestros <span style={{ color: colors.secondary }}>Valores</span>
                            </h2>
                        </div>

                        <div className="values-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                            {[
                                { icon: '🤝', title: 'Colaboración', desc: 'Creemos que juntos llegamos más lejos. Fomentamos redes de apoyo real entre nuestros miembros.', color: colors.primary },
                                { icon: '💡', title: 'Innovación', desc: 'Impulsamos el pensamiento creativo y la búsqueda constante de nuevas soluciones con impacto.', color: colors.secondary },
                                { icon: '🌱', title: 'Crecimiento', desc: 'Acompañamos cada etapa del camino, desde la idea hasta la consolidación del negocio.', color: colors.secondaryLight },
                                { icon: '🎯', title: 'Propósito', desc: 'Cada acción tiene un porqué. Trabajamos con intención y orientados a resultados concretos.', color: colors.accentDark },
                                { icon: '🔐', title: 'Confianza', desc: 'Construimos relaciones auténticas basadas en transparencia, compromiso y respeto mutuo.', color: colors.primaryDark },
                                { icon: '🌍', title: 'Impacto', desc: 'Medimos nuestro éxito por la transformación real que generamos en personas y comunidades.', color: colors.secondary },
                            ].map((v, i) => (
                                <div key={v.title} className={`fade-up ${valuesInView ? 'in' : ''}`} style={{ transitionDelay: `${i * 0.08}s` }}>
                                    <BorderGlow
                                        borderRadius={20}
                                        backgroundColor={isDark ? '#150d24' : '#ffffff'}
                                        style={{ height: '100%' }}
                                    >
                                        <div style={{
                                            padding: '28px 24px',
                                            display: 'flex', gap: '16px', alignItems: 'flex-start',
                                            height: '100%',
                                        }}>
                                            <div style={{
                                                width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                                                background: `${v.color}22`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '1.3rem',
                                            }}>
                                                {v.icon}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', fontSize: '1rem', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>{v.title}</div>
                                                <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.65' }}>{v.desc}</p>
                                            </div>
                                        </div>
                                    </BorderGlow>
                                </div>
                            ))}
                        </div>

                        <div className={`fade-up ${valuesInView ? 'in' : ''}`} style={{ marginTop: '64px', display: 'flex', justifyContent: 'center' }}>
                            <BrandStripes size="md" />
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default Aboutus;